import * as React from 'react';
import { useSelector } from 'react-redux';
import { authSelector } from 'store/auth/selector';
import { ContextualMenu, DefaultButton, Dialog, DialogFooter, DialogType, PrimaryButton } from '@fluentui/react';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { formStyles } from 'styles/formStyles';
import {
    addBestellungPosition,
    BestellungPosition,
    defaultBestellungPosition,
    updateBestellungPosition,
    useBestellungPositionList,
} from 'api/bestellungPosition';
import { SavingState } from 'api/savingState';
import { useIsMounted } from 'hooks/useIsMounted';
import { cloneDeep, isEqual } from 'lodash';
import { FormTextField, FormTextFieldOptions } from 'formComponents/FormTextField';
import { EinheitByCode } from 'formComponents/lookups/EinheitByCode';
import { FormTextarea, FormTextareaOptions } from 'formComponents/FormTextarea';
import { Timestamps } from 'formComponents/Timestamps';
import { LoadingIndicator } from 'layout/LoadingIndicator/LoadingIndicator';
import { Kunde } from 'api/kunde';
import useArtikelCalculator from 'hooks/useArtikelCalculator';
import { LieferArt } from 'formComponents/lookups/LieferArt';
import { Bestellung } from 'api/bestellung';
import { FormDecimalField, FormDecimalFieldOptions } from 'formComponents/FormDecimalField';
import { logDifference } from 'utils/helper';
import { BestellungPositionChangeMenge } from './BestellungPositionChangeMenge';
import { ArtikelDetailSelect } from '../Lager/ArtikelDetail/ArtikelDetailSelect';

const styles = mergeStyleSets({
    artikelLine: {
        marginTop: 5,
        display: 'grid',
        // gridTemplateColumns: '80px 1fr 3fr',
        gridTemplateColumns: '80px 1fr',
        gridColumnGap: 10,
    },
    textLine: {
        marginTop: 5,
        display: 'grid',
        gridTemplateColumns: '1fr max-content',
        gridColumnGap: 10,
    },
    addLine: {
        marginTop: 5,
        display: 'grid',
        gridTemplateColumns: '6fr 1fr',
        gridColumnGap: 10,
    },
    posLine: {
        marginTop: 5,
        display: 'grid',
        gridTemplateColumns: 'repeat(7, 1fr)',
        gridColumnGap: 10,
    },
    lastLine: {
        marginTop: 5,
        display: 'grid',
        gridTemplateColumns: '3fr 1fr',
        gridColumnGap: 10,
    },
    varianteHeader: {
        marginTop: 15,
        textAlign: 'center',
        fontSize: 14,
        fontWeight: 700,
        backgroundColor: '#EEEEEE',
        paddingTop: 10,
    },
    variante: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        gridColumnGap: 10,
        backgroundColor: '#EEEEEE',
        padding: '0 10px 10px 10px',
    },
});

interface BestellungPositionEditProps {
    kunde: Kunde;
    bestellung: Bestellung;
    bNr: string;
    existingBestellungPositionen: BestellungPosition[];
    pos: number; // -1 = new
    readOnly?: boolean;
    onClose: (refetch: boolean) => void;
}

export const BestellungPositionEdit: React.FC<BestellungPositionEditProps> = ({
    kunde,
    bestellung,
    bNr,
    existingBestellungPositionen,
    pos,
    readOnly = false,
    onClose,
}) => {
    const auth = useSelector(authSelector);

    const [savingState, setSavingState] = React.useState(SavingState.notChanged);
    const [validationError, setValidationError] = React.useState<string | null>(null);
    const isMounted = useIsMounted();
    const [showChangeMenge, setShowChangeMenge] = React.useState(false);

    const artikelCalculator = useArtikelCalculator();

    const bestellungPositionList = useBestellungPositionList(
        {
            bNr,
            pos,
        },
        { enabled: pos !== -1 },
    );

    const [bestellungPosition, setBestellungPosition] = React.useState<BestellungPosition | null>(null);

    React.useEffect(() => {
        if (!bestellungPositionList.isLoading && !bestellungPositionList.isFetching && bestellungPosition === null) {
            if (bestellungPositionList.data?.length === 1) {
                setBestellungPosition(cloneDeep(bestellungPositionList.data[0]));
                setSavingState(SavingState.notChanged);
            } else {
                setBestellungPosition({
                    ...defaultBestellungPosition,
                    bNr,
                    pos,
                    posMwst: kunde.land === 'AT' ? 20 : 0,
                });
                setSavingState(SavingState.notSaved);
            }
        }
    }, [bestellungPosition, bestellungPositionList, bNr, pos, auth, kunde]);

    React.useEffect(() => {
        if (
            savingState === SavingState.notChanged &&
            !bestellungPositionList.isLoading &&
            !bestellungPositionList.isFetching &&
            bestellungPositionList.data?.length === 1 &&
            bestellungPosition !== null &&
            !isEqual(bestellungPositionList.data[0], bestellungPosition)
        ) {
            setSavingState(SavingState.notSaved);
            logDifference('bestellungPosition', bestellungPosition, bestellungPositionList.data[0]);
        }
    }, [savingState, bestellungPositionList, bestellungPosition]);

    React.useEffect(() => {
        if (bestellungPosition) {
            if (bestellungPosition.posMenge && bestellungPosition.posEPreis) {
                const netto = Math.round(bestellungPosition.posMenge * bestellungPosition.posEPreis * 100) / 100;
                const mwstFactor = bestellungPosition.posMwst ? 1 + bestellungPosition?.posMwst / 100 : 1;
                const brutto = bestellungPosition.posMwst ? Math.round(netto * mwstFactor * 100) / 100 : netto;

                setBestellungPosition({
                    ...bestellungPosition,
                    posNetto: netto,
                    posBrutto: brutto,
                });
            } else {
                setBestellungPosition({
                    ...bestellungPosition,
                    posNetto: 0,
                    posBrutto: 0,
                });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bestellungPosition?.posMenge, bestellungPosition?.posEPreis, bestellungPosition?.posMwst]);

    const validate = (isNew: boolean): boolean => {
        if (
            isNew &&
            bestellungPosition?.artikelNr &&
            existingBestellungPositionen.find((p) => p.artikelNr === bestellungPosition?.artikelNr)
        ) {
            setValidationError('Dieser Artikel ist bereits in der Liste.');
            return false;
        }

        if (!bestellungPosition?.posText?.trim()) {
            setValidationError('Bitte geben Sie einen Text ein.');
            return false;
        }
        if (validationError !== null) {
            setValidationError(null);
        }
        return true;
    };

    const save = () => {
        if (bestellungPosition && savingState !== SavingState.saving) {
            if (validate(bestellungPosition.pos === -1)) {
                setSavingState(SavingState.saving);
                if (bestellungPosition.pos === -1) {
                    addBestellungPosition(bestellungPosition)
                        .then(() => {
                            setSavingState(SavingState.saved);
                            if (isMounted) {
                                setSavingState(SavingState.notChanged);
                            }
                            onClose(true);
                        })
                        .catch((error) => {
                            console.error(error);
                            setSavingState(SavingState.error);
                        });
                } else {
                    updateBestellungPosition(bestellungPosition)
                        .then(() => {
                            setSavingState(SavingState.saved);
                            if (isMounted) {
                                setSavingState(SavingState.notChanged);
                            }
                            onClose(true);
                        })
                        .catch((error) => {
                            console.error(error);
                            setSavingState(SavingState.error);
                        });
                }
            }
        }
    };

    const renderTextField = (column: keyof BestellungPosition, label: string, options: FormTextFieldOptions = {}) => (
        <FormTextField
            value={(bestellungPosition?.[column] || '') as string}
            savingState={savingState}
            options={{ ...options, disabled: options.disabled || readOnly }}
            onChange={(newValue): void => {
                if (bestellungPosition) {
                    setBestellungPosition({
                        ...bestellungPosition,
                        [column]: newValue,
                    });
                }
            }}
            label={label}
        />
    );

    const renderDecimalField = (
        column: keyof BestellungPosition,
        label: string,
        options: FormDecimalFieldOptions = {},
    ) => (
        <FormDecimalField
            value={(bestellungPosition?.[column] || '') as number}
            savingState={savingState}
            options={{ ...options, disabled: options.disabled || readOnly }}
            onChange={(newValue): void => {
                if (options.onChange) {
                    options.onChange(newValue);
                } else if (bestellungPosition) {
                    setBestellungPosition({
                        ...bestellungPosition,
                        [column]: newValue,
                    });
                }
            }}
            label={label}
        />
    );

    const renderTextarea = (column: keyof BestellungPosition, label: string, options: FormTextareaOptions = {}) => (
        <FormTextarea
            value={bestellungPosition?.[column] as string}
            savingState={savingState}
            options={{ ...options, disabled: options.disabled || readOnly }}
            onChange={(newValue): void => {
                if (bestellungPosition) {
                    setBestellungPosition({
                        ...bestellungPosition,
                        [column]: newValue,
                    });
                }
            }}
            label={label}
        />
    );

    const renderArtikelDetailSelect = () => (
        <ArtikelDetailSelect
            artikelGruppeNr={bestellungPosition?.artikelGruppeNr}
            artikelNr={bestellungPosition?.artikelNr}
            disabled={bestellungPosition?.pos !== -1}
            onChange={async (newArtikelDetail) => {
                if (bestellungPosition) {
                    if (!newArtikelDetail || !newArtikelDetail.artikel) {
                        return;
                    }

                    const { ePreis, ekPreis } = await artikelCalculator.getPreise(
                        newArtikelDetail.artikelGruppeNr,
                        newArtikelDetail.artikelNr,
                        newArtikelDetail.artikel.einheit,
                    );

                    setBestellungPosition({
                        ...bestellungPosition,
                        artikelGruppeNr: newArtikelDetail.artikelGruppeNr,
                        artikelNr: newArtikelDetail.artikelNr,
                        posText: newArtikelDetail.artikel.text,
                        posEkPreis: ekPreis,
                        posEPreis: ePreis,
                        posEinh: newArtikelDetail.artikel.einheit || '',
                        lArt: newArtikelDetail.lagerIntNr === 0 ? 'D' : 'R',
                    });
                }
            }}
        />
    );

    const renderEinheit = (
        column: keyof BestellungPosition,
        ekPreisColumn: keyof BestellungPosition,
        ePreisColumn: keyof BestellungPosition,
        label: string,
    ) => (
        <EinheitByCode
            label={label}
            disabled={savingState === SavingState.saving || readOnly}
            value={(bestellungPosition?.[column] as string) || ''}
            menuPlacement="top"
            onChange={async (newEinheit) => {
                const { ePreis, ekPreis } = await artikelCalculator.getPreise(
                    bestellungPosition?.artikelGruppeNr,
                    bestellungPosition?.artikelNr,
                    newEinheit,
                );

                if (bestellungPosition) {
                    setBestellungPosition({
                        ...bestellungPosition,
                        [column]: newEinheit,
                        [ekPreisColumn]: ekPreis,
                        [ePreisColumn]: ePreis,
                    });
                }
            }}
        />
    );

    const renderLieferArt = () => (
        <LieferArt
            allowEmpty
            disabled={savingState === SavingState.saving || readOnly || pos !== -1}
            value={(bestellungPosition?.lArt as string) || ''}
            menuPlacement="top"
            onChange={(newValue) => {
                if (bestellungPosition) {
                    setBestellungPosition({
                        ...bestellungPosition,
                        lArt: newValue || '',
                    });
                }
            }}
        />
    );

    return (
        <>
            <Dialog
                hidden={false}
                title="Bestellposition"
                dialogContentProps={{
                    type: DialogType.normal,
                }}
                modalProps={{
                    isBlocking: true,
                    dragOptions: {
                        moveMenuItemText: 'Move',
                        closeMenuItemText: 'Close',
                        menu: ContextualMenu,
                    },
                }}
                minWidth={1100}
                onDismiss={() => {
                    onClose(false);
                }}
            >
                <form className={formStyles.form}>
                    <div className={styles.artikelLine}>
                        <div>{renderTextField('pos', 'Position', { numeric: true, disabled: true })}</div>
                        <div>{renderArtikelDetailSelect()}</div>
                    </div>
                    <div className={formStyles.line}>
                        <div>{renderTextField('posText', 'Text', { maxLength: 100 })}</div>
                    </div>
                    <div className={formStyles.line}>{renderTextarea('posBem', 'Bemerkung zur Position')}</div>
                    <div className={styles.addLine}>
                        <div />
                        <div>{renderLieferArt()}</div>
                    </div>
                    <div className={styles.posLine}>
                        <div>{renderEinheit('posEinh', 'posEkPreis', 'posEPreis', 'Einheit')}</div>
                        <div>
                            {renderDecimalField('posMenge', 'Menge', {
                                fixed: 3,
                                disabled: pos !== -1,
                                onChange: (newValue) => {
                                    if (bestellungPosition) {
                                        setBestellungPosition({
                                            ...bestellungPosition,
                                            posMenge: newValue,
                                            offen: newValue,
                                        });
                                    }
                                },
                            })}
                        </div>
                        <div>
                            {renderDecimalField('offen', 'Offen', {
                                // cannot disable it now because existing records need to be updated manually
                                // disabled: pos !== -1,
                                fixed: 3,
                                disabled: true,
                            })}
                        </div>
                        <div>{renderDecimalField('posEPreis', 'EK-Preis', { fixed: 3 })}</div>
                        <div>{renderDecimalField('posNetto', 'EK Netto', { disabled: true })}</div>
                        <div>{renderDecimalField('posMwst', 'MwSt.', { fixed: 0 })}</div>
                        <div>{renderDecimalField('posBrutto', 'EK Brutto', { disabled: true })}</div>
                    </div>

                    <Timestamps
                        modifiedDate={bestellungPosition?.updDat}
                        modifiedDecimalTime={bestellungPosition?.updUhr}
                        modifiedUser={bestellungPosition?.updUser}
                        showCreated={false}
                    />

                    {validationError && <div className={formStyles.error}>{validationError}</div>}
                    {savingState === SavingState.error && (
                        <div className={formStyles.error}>Fehler beim Speichern.</div>
                    )}

                    <DialogFooter>
                        <DefaultButton
                            className={formStyles.migrateButton}
                            onClick={() => setShowChangeMenge(true)}
                            text="Lieferart / Menge ändern"
                            disabled={
                                savingState !== SavingState.notChanged ||
                                bestellungPositionList.isLoading ||
                                !bestellungPosition?.pos
                            }
                        />
                        {savingState === SavingState.saving ? (
                            <PrimaryButton text="Speichere..." disabled />
                        ) : (
                            <PrimaryButton
                                onClick={save}
                                text="Speichern"
                                disabled={savingState === SavingState.notChanged || bestellungPositionList.isLoading}
                            />
                        )}
                    </DialogFooter>
                </form>
                {(bestellungPositionList.isLoading || bestellungPositionList.isFetching || !bestellungPosition) && (
                    <LoadingIndicator />
                )}
            </Dialog>
            {showChangeMenge && bestellungPosition && (
                <BestellungPositionChangeMenge
                    bestellung={bestellung}
                    bestellungPosition={bestellungPosition}
                    onClose={(refetch) => {
                        setShowChangeMenge(false);
                        if (refetch) {
                            onClose(true);
                        }
                    }}
                />
            )}
        </>
    );
};
